import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Copy, Check, ExternalLink, Pencil, Globe, UserPlus, Megaphone, FileSpreadsheet,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ClientFormDialog } from "@/components/feature/media/client-form-dialog";
import { BudgetCard } from "@/components/feature/media/budget-card";
import { PortalCredentialsForm } from "@/components/feature/media/portal-credentials-form";
import { media } from "@/lib/api";

export const Route = createFileRoute("/_auth/clients/$clientId")({
  component: ClientDetail,
});

function ClientDetail() {
  const { clientId } = Route.useParams();
  const id = Number(clientId);
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const clientQ = useQuery({ queryKey: ["media", "clients", "one", id], queryFn: () => media.getClient(id) });
  const campaignsQ = useQuery({
    queryKey: ["media", "campaigns", "by_client", id],
    queryFn: () => media.listCampaigns({ client_id: id, limit: 200 }),
  });
  const usersQ = useQuery({ queryKey: ["media", "clients", "users", id], queryFn: () => media.listClientUsers(id) });

  const portal = useMutation({
    mutationFn: (enable: boolean) => enable ? media.publishClientPortal(id, null) : media.unpublishClientPortal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "clients"] });
      toast.success("Portal actualizado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (clientQ.isLoading) return <Skeleton className="h-64" />;
  if (!clientQ.data) return <div className="text-ink-500">Cliente no encontrado.</div>;
  const c = clientQ.data;
  const portalUrl = `${window.location.origin}${media.clientPortalUrlBySlug(c.slug)}`;

  function copyUrl() {
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <>
      <Link to="/clients" className="text-xs text-ink-500 hover:text-ink-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Clientes
      </Link>

      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-lg bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center shrink-0">
            <Building2 size={22} className="text-brand-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-ink-900 dark:text-white truncate">{c.name}</h2>
            <div className="text-xs font-mono text-ink-500">{c.code} · /r/{c.slug}</div>
          </div>
          <Badge variant={c.is_active ? "default" : "outline"}>{c.is_active ? "Activo" : "Inactivo"}</Badge>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" asChild
            title="Exportar todas las inserciones con costo, precio y margen para conciliación">
            <a href={media.conciliationExportXlsxUrl(id)} download>
              <FileSpreadsheet size={14} /> Conciliación
            </a>
          </Button>
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil size={14} /> Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Portal */}
        <Card>
          <CardContent className="py-5 space-y-3">
            <div className="font-semibold flex items-center gap-2">
              <Globe size={16} className="text-brand-600" /> Portal de transparencia
            </div>
            <div className="flex gap-2">
              <Input value={portalUrl} readOnly className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={copyUrl} title="Copiar">
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </Button>
              <Button variant="outline" size="icon" asChild title="Abrir">
                <a href={portalUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /></a>
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {c.portal_enabled ? (
                <>
                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Activo</Badge>
                  <Button variant="destructive" size="sm" disabled={portal.isPending}
                    onClick={() => portal.mutate(false)}>Despublicar</Button>
                </>
              ) : (
                <Button size="sm" disabled={portal.isPending} onClick={() => portal.mutate(true)}>
                  <Globe size={14} /> Publicar portal
                </Button>
              )}
            </div>
            {c.portal_enabled && <PortalCredentialsForm client={c} />}
          </CardContent>
        </Card>

        {/* Usuarios scoped */}
        <Card>
          <CardContent className="py-5 space-y-3">
            <div className="font-semibold flex items-center gap-2">
              <UserPlus size={16} className="text-brand-600" /> Usuarios del cliente
            </div>
            <div className="space-y-1 text-sm">
              {(usersQ.data ?? []).map(u => (
                <div key={u.id} className="flex items-center justify-between">
                  <span className="truncate">{u.name} <span className="text-ink-400">· {u.email}</span></span>
                  <Badge variant="outline" className="capitalize">{u.role}</Badge>
                </div>
              ))}
              {!(usersQ.data ?? []).length && <div className="text-xs text-ink-400">Sin usuarios asignados.</div>}
            </div>
            <InviteUserForm clientId={id} />
          </CardContent>
        </Card>
      </div>

      {/* Presupuesto */}
      <div className="mt-4">
        <BudgetCard clientId={id} />
      </div>

      {/* Campañas */}
      <div className="mt-5">
        <div className="font-semibold flex items-center gap-2 mb-2">
          <Megaphone size={16} className="text-brand-600" /> Campañas
        </div>
        <div className="space-y-2">
          {(campaignsQ.data ?? []).map(camp => (
            <Link key={camp.id} to="/media/campaigns/$campaignId" params={{ campaignId: String(camp.id) }} className="block">
              <Card className="hover:border-brand-300">
                <CardContent className="py-3 flex items-center justify-between">
                  <span className="font-medium">{camp.name}</span>
                  <Badge variant="outline" className="capitalize">{camp.status}</Badge>
                </CardContent>
              </Card>
            </Link>
          ))}
          {!(campaignsQ.data ?? []).length && (
            <div className="text-xs text-ink-400 border-2 border-dashed border-ink-200 rounded-lg py-6 text-center">
              Sin campañas para este cliente.
            </div>
          )}
        </div>
      </div>

      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={c} />
    </>
  );
}

function InviteUserForm({ clientId }: { clientId: number }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "cliente" as "cliente" | "supervisor" });

  const invite = useMutation({
    mutationFn: () => media.inviteClientUser(clientId, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "clients", "users", clientId] });
      toast.success("Usuario invitado");
      setForm({ name: "", email: "", password: "", role: "cliente" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit() {
    if (form.name.trim().length < 2) { toast.error("Nombre requerido"); return; }
    if (!form.email) { toast.error("Email requerido"); return; }
    if (form.password.length < 8) { toast.error("Contraseña mínimo 8 caracteres"); return; }
    invite.mutate();
  }

  return (
    <div className="pt-2 border-t border-ink-100 dark:border-ink-800 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Nombre</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Contraseña</Label>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Rol</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v as "cliente" | "supervisor" })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cliente">Cliente</SelectItem>
              <SelectItem value="supervisor">Supervisor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button size="sm" onClick={submit} disabled={invite.isPending} className="w-full">
        <UserPlus size={14} /> Invitar usuario
      </Button>
    </div>
  );
}
